import type { AgeClass, AnimalSex } from "@/api/cases/CasesApi";
import { useTheme } from "@/theme/ThemeProvider";
import { FloatingLabelInput } from "@/ui/components/FloatingLabelInput";
import { SegmentedChoice } from "@/ui/components/SegmentedChoice";
import {
  INTAKE_WEIGHT_FIELD_WIDTH,
  INTAKE_WEIGHT_HINT,
  INTAKE_WEIGHT_LABEL,
  INTAKE_WEIGHT_SUFFIX,
} from "@/ui/intake/intakeCopy";
import { INTAKE_WEIGHT_LOCKED_HINT } from "@/ui/intake/intakeWeight";
import { batchEnterTarget } from "@/ui/intake/batchAnimalLayout";
import { useCoarseTarget } from "@/ui/intake/useCoarseTarget";
import { useRef, useState } from "react";
import { Pressable, Text, TextInput, View } from "react-native";

const SEX_OPTIONS: { value: AnimalSex; label: string }[] = [
  { value: "unknown", label: "Unbekannt" },
  { value: "male", label: "Männlich" },
  { value: "female", label: "Weiblich" },
];

const AGE_OPTIONS: { value: AgeClass; label: string }[] = [
  { value: "unclear", label: "Unklar" },
  { value: "baby", label: "Baby" },
  { value: "juvenile", label: "Jungtier" },
  { value: "adult", label: "Adult" },
];

export type BatchAnimalPatch = {
  name?: string;
  sex?: AnimalSex;
  ageClass?: AgeClass;
  notes?: string;
  intakeWeight?: string;
};

/**
 * Darstellung eines Tiers in Schritt 2. Die Werte bleiben im Entwurf der Sammelaufnahme.
 * Zeile und Stapel nutzen denselben Baum; nur Richtung und Umbruch wechseln.
 */
export function BatchAnimalEditor({
  index,
  count,
  name,
  sex,
  ageClass,
  intakeWeight,
  notes,
  weightLocked,
  row,
  onChange,
  onRemove,
  weightRef,
  nameRef,
  noteRef,
  onFocusNext,
  onFocusSave,
}: {
  index: number;
  count: number;
  name: string;
  sex: AnimalSex;
  ageClass: AgeClass;
  intakeWeight: string;
  notes: string;
  weightLocked: boolean;
  /** Ab 768 px Containerbreite eine umbrechende Zeile, darunter eine Spalte. */
  row: boolean;
  onChange: (patch: BatchAnimalPatch) => void;
  onRemove?: () => void;
  weightRef: (node: TextInput | null) => void;
  nameRef: (node: TextInput | null) => void;
  noteRef: (node: TextInput | null) => void;
  onFocusNext: () => void;
  onFocusSave: () => void;
}) {
  const { theme } = useTheme();
  const coarse = useCoarseTarget();
  const tier = `Tier ${index + 1}`;
  const [notesOpen, setNotesOpen] = useState(() => notes.trim() !== "");
  const hit = coarse ? theme.control.heightTouch : theme.control.heightDense;
  const nameNode = useRef<TextInput | null>(null);
  const noteNode = useRef<TextInput | null>(null);

  function follow(field: "weight" | "name" | "note") {
    const target = batchEnterTarget(index, count, notesOpen, field);
    if (target === "name") nameNode.current?.focus();
    else if (target === "note") noteNode.current?.focus();
    else if (target === "next") onFocusNext();
    else onFocusSave();
  }

  const remove = onRemove ? (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={`${tier} entfernen`}
      onPress={onRemove}
      style={{ minHeight: hit, justifyContent: "center", flexShrink: 0 }}
    >
      <Text style={{ color: theme.color.dangerText, fontSize: theme.fontSize.small, fontWeight: "700" }}>
        Entfernen
      </Text>
    </Pressable>
  ) : null;

  const weight = (
    <View testID="batch-field-weight" style={{ flexShrink: 0, maxWidth: "100%" }}>
      <FloatingLabelInput
        ref={weightRef}
        label={INTAKE_WEIGHT_LABEL}
        accessibilityLabel={`${INTAKE_WEIGHT_LABEL}, ${tier}`}
        value={intakeWeight}
        onChangeText={(value) => onChange({ intakeWeight: value })}
        editable={!weightLocked}
        width="weight"
        containerStyle={{ width: INTAKE_WEIGHT_FIELD_WIDTH }}
        rightElement={
          <Text style={{ color: theme.color.muted, fontWeight: "700" }}>{INTAKE_WEIGHT_SUFFIX}</Text>
        }
        hint={weightLocked ? undefined : INTAKE_WEIGHT_HINT}
        keyboardType="decimal-pad"
        returnKeyType="next"
        blurOnSubmit={false}
        onSubmitEditing={() => follow("weight")}
      />
      {weightLocked ? (
        <Text
          accessibilityLiveRegion="polite"
          style={{ color: theme.color.warningText, fontSize: theme.fontSize.small }}
        >
          {INTAKE_WEIGHT_LOCKED_HINT}
        </Text>
      ) : null}
    </View>
  );

  const choice = {
    layout: "wrap" as const,
    idleBorderColor: theme.color.inputBorder,
  };

  const sexField = (
    <View testID="batch-field-sex" style={{ flexShrink: 0, maxWidth: "100%" }}>
      <SegmentedChoice
        accessibilityLabel={`Geschlecht, ${tier}`}
        options={SEX_OPTIONS}
        value={sex}
        onChange={(value) => onChange({ sex: value })}
        {...choice}
      />
    </View>
  );

  const ageField = (
    <View testID="batch-field-age" style={{ flexShrink: 0, maxWidth: "100%" }}>
      <SegmentedChoice
        accessibilityLabel={`Altersklasse, ${tier}`}
        options={AGE_OPTIONS}
        value={ageClass}
        onChange={(value) => onChange({ ageClass: value })}
        {...choice}
      />
    </View>
  );

  const nameField = (
    <View
      testID="batch-field-name"
      style={{
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: row ? INTAKE_WEIGHT_FIELD_WIDTH : undefined,
        minWidth: row ? INTAKE_WEIGHT_FIELD_WIDTH : undefined,
        maxWidth: "100%",
      }}
    >
      <FloatingLabelInput
        ref={(node) => {
          nameNode.current = node;
          nameRef(node);
        }}
        label="Name"
        accessibilityLabel={`Name, ${tier}`}
        value={name}
        onChangeText={(value) => onChange({ name: value })}
        hint="Optional. Kurzname für die Akte."
        returnKeyType="next"
        blurOnSubmit={false}
        onSubmitEditing={() => follow("name")}
      />
    </View>
  );

  const noteToggle = (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={`Notiz zu ${name.trim() || tier}`}
      accessibilityState={{ expanded: notesOpen }}
      onPress={() => setNotesOpen((open) => !open)}
      style={{ minHeight: hit, justifyContent: "center", paddingHorizontal: theme.space.sm, flexShrink: 0 }}
    >
      <Text style={{ color: theme.color.accent, fontSize: theme.fontSize.small, fontWeight: "700" }}>
        {notes.trim() ? "Notiz (ausgefüllt)" : "Notiz"}
      </Text>
    </Pressable>
  );

  const noteField = notesOpen ? (
    <FloatingLabelInput
      ref={(node) => {
        noteNode.current = node;
        noteRef(node);
      }}
      label="Notizen"
      accessibilityLabel={`Notizen, ${tier}`}
      value={notes}
      onChangeText={(value) => onChange({ notes: value })}
      hint="Optional. Eine Zeile."
      returnKeyType={index === count - 1 ? "next" : "done"}
      blurOnSubmit={false}
      onSubmitEditing={() => follow("note")}
    />
  ) : null;

  return (
    <View
      testID={row ? "batch-animal-row" : "batch-animal-stack"}
      role="group"
      accessibilityLabel={tier}
      style={{ gap: theme.space.xs, maxWidth: "100%" }}
    >
      <View
        testID="batch-animal-fields"
        style={{
          flexDirection: row ? "row" : "column",
          flexWrap: row ? "wrap" : "nowrap",
          alignItems: row ? "center" : "stretch",
          minWidth: 0,
          maxWidth: "100%",
          width: "100%",
          gap: theme.space.sm,
        }}
      >
        <Text style={{ color: theme.color.muted, fontSize: theme.fontSize.small, fontWeight: "700", flexShrink: 0 }}>
          {tier}
        </Text>
        {weight}
        {sexField}
        {ageField}
        {nameField}
        {noteToggle}
        {remove}
      </View>
      {noteField}
    </View>
  );
}
